import { useLayoutEffect } from "react";
import type { ProductKey } from "../../../shared/products/keys";

/*
 * A product's own palette (docs/superpowers/specs/2026-10-06-skyperformance-wireframe-palette.md).
 *
 * The palette is a stylesheet that re-maps the app's design tokens under
 * `:root[data-product="<product>"]` (src/products/performance/theme.css, imported once by
 * src/index.tsx). This module only decides when that attribute is on <html>: it follows the shell's
 * product (useActiveProductState().product, the same one that picks the sidebar and the wordmark),
 * so a shared page drawn in Performance is themed with it.
 *
 * <html> rather than the layout's root: every skypoint-ui overlay (dialog, menu, dropdown, toast)
 * portals under <body>, outside the layout, and must take the same palette.
 *
 * skyHire has no palette of its own: on a hire shell the attribute is absent and the app's default
 * tokens apply exactly as before.
 */

export const PRODUCT_THEME_ATTRIBUTE = "data-product";

/** The products that draw their own palette. */
const THEMED_PRODUCTS: readonly ProductKey[] = ["skyperformance", "skyperformance_it"];

/** The palette a product's shell draws, or null for the app's default (skyHire). */
export function productThemeFor(product: ProductKey): ProductKey | null {
  return THEMED_PRODUCTS.includes(product) ? product : null;
}

/**
 * Which wordmark the header takes. Every product's shell draws the same dark header bar
 * (docs/superpowers/specs/2026-10-08-unified-shell-settings-hub-design.md, D1), so every product
 * takes its white-text ("light") variant. The product is still asked so a future header that differs
 * per product has one place to say so.
 */
export function wordmarkVariantFor(_product: ProductKey): "light" | "dark" {
  return "light";
}

/**
 * Puts the shell's palette on <html>, and takes it off on a hire shell or when the shell unmounts.
 * A layout effect, so the first themed frame is never painted with the default header.
 */
export function useProductThemeAttribute(product: ProductKey): void {
  useLayoutEffect(() => {
    const root = document.documentElement;
    const theme = productThemeFor(product);
    if (theme) root.setAttribute(PRODUCT_THEME_ATTRIBUTE, theme);
    else root.removeAttribute(PRODUCT_THEME_ATTRIBUTE);
    return () => root.removeAttribute(PRODUCT_THEME_ATTRIBUTE);
  }, [product]);
}
